import { Alert, Box, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import MetadataTable, { MetadataField } from "./MetadataTable";
import { formatDataPullDate } from "./formatDataPullDate";
import {
  formatDataTypeLabel,
  ModalRoot,
  ModalOverlay,
  ModalContentContainer,
  ModalCloseButton,
  Button,
} from "@atlas-int/ui";
import { spacing, text, shadow } from "@atlas-int/tokens";
import styled from "@emotion/styled";
import { useState } from "react";
import CitationIcon from "./citation.svg?react";

/** MUI `Dialog` + `Button variant="contained"` on production (data-downloads). */
const MUI_CONTAINED_BG = "#37474f";
const MUI_CONTAINED_BG_HOVER = "#263238";

const DatasetModalActionButton = styled(Button)`
  && {
    background-color: ${MUI_CONTAINED_BG};
    color: ${text.onDark};
    border: none;
    border-radius: 0;
    text-transform: none;
    font-weight: bold;
    padding: 8px 24px;
    &:hover:not(:disabled) {
      background-color: ${MUI_CONTAINED_BG_HOVER};
    }
  }
`;

const DatasetModalCopyButton = styled(Button)`
  && {
    background-color: ${MUI_CONTAINED_BG};
    color: ${text.onDark};
    border: none;
    border-radius: 0;
    text-transform: none;
    min-width: 100px;
    &:hover:not(:disabled) {
      background-color: ${MUI_CONTAINED_BG_HOVER};
    }
  }
`;

// Dataset name mapping based on repository value
const datasetNameMapping: Record<string, string> = {
  hs92: "International Trade Data (HS92)",
  hs12: "International Trade Data (HS12)",
  hs22: "International Trade Data (HS22)",
  sitc: "International Trade Data (SITC, Rev. 2)",
  services_unilateral: "International Trade Data (Services)",
  classification: "Classifications Data",
  rankings: "Growth Projections and Complexity Rankings",
  product_space: "Product Space Networks",
};

interface DatasetModalProps {
  open: boolean;
  onClose: () => void;
  dataset: any;
}

const DatasetModal = ({ open, onClose, dataset }: DatasetModalProps) => {
  const [copied, setCopied] = useState(false);

  if (!dataset || !open) return null;
  const columns = dataset.originalData?.columns || dataset.Columns || [];

  const metadataFields: MetadataField[] = columns.map((column: any) => ({
    fieldName: column.columnName || "",
    description: column.description || "",
    dataType: column.dataType || "",
    notes: column.notes || "",
  }));

  const displayMetadata = metadataFields || [];

  const fileName = dataset.originalData?.displayName;

  const repo = dataset.originalData?.repo || "";
  const datasetDisplayName = datasetNameMapping[repo] || "";

  const publicationDate = dataset.originalData?.dvPublicationDate || "";
  const publicationYear = publicationDate
    ? new Date(publicationDate).getFullYear()
    : new Date().getFullYear();

  const doi = dataset.originalData?.doi || "";
  const doiUrl = doi
    ? `https://doi.org/${doi}`
    : "http://www.atlas.hks.harvard.edu";

  const yearMin = dataset.originalData?.yearMin;
  const yearMax = dataset.originalData?.yearMax;
  const yearsRange =
    yearMin && yearMax ? `${yearMin}-${yearMax}` : "various years";

  const dataType = dataset["Data Type (tag)"];
  const classification = dataset["Classification (tag)"] || "";

  const productLevel = dataset["Product Level"] || "";

  const createParentheticalInfo = () => {
    const parts = [];

    if (dataType) {
      parts.push(formatDataTypeLabel(dataType));
    }

    if (classification) {
      parts.push(classification);
    }

    if (productLevel && productLevel !== "N/A") {
      parts.push(`${productLevel} digit`);
    }

    parts.push(yearsRange);

    return parts.length > 0 ? `(${parts.join(", ")})` : "";
  };

  const parentheticalInfo = createParentheticalInfo();

  const dvFileId = dataset.originalData?.dvFileId || dataset["DV File ID"];

  const handleDownload = () => {
    if (dvFileId) {
      const downloadUrl = `https://dataverse.harvard.edu/api/access/datafile/${dvFileId}`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    onClose();
  };

  const handleCopyCitation = () => {
    const citationText = `The Growth Lab at Harvard University, ${publicationYear}, "${datasetDisplayName}", ${doiUrl}, Harvard Dataverse`;

    navigator.clipboard
      .writeText(citationText)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  return (
    <ModalRoot onClose={onClose}>
      <ModalOverlay onClick={onClose}>
        <ModalContentContainer
          $width="80%"
          onClick={(e) => e.stopPropagation()}
          style={{
            height: "90vh",
            overflow: "hidden",
            border: "none",
            boxShadow: shadow.md,
          }}
        >
          {/* Close button row */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: `${spacing[4]} ${spacing[8]} 0`,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <ModalCloseButton
              onClick={onClose}
              style={{ color: text.primary }}
            />
          </div>
          {/* Content area — `px: 4` matches master `Dialog` content `Box` */}
          <Box
            sx={{
              px: 4,
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 0,
                  mb: 2,
                  fontSize: "28px",
                  fontWeight: 600,
                }}
              >
                <Box component="span" sx={{ flex: 1, color: "text.primary" }}>
                  <Box component="span" sx={{ fontSize: 26, fontWeight: "normal" }}>
                    {datasetDisplayName}
                  </Box>{" "}
                  <Box component="span" sx={{ fontSize: 20, fontWeight: "bold" }}>
                    {parentheticalInfo}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DatasetModalActionButton
                    variant="primary"
                    onClick={handleDownload}
                    disabled={!dvFileId}
                  >
                    <DownloadIcon fontSize="small" />
                    Download Data
                  </DatasetModalActionButton>
                </Box>
              </Box>

              {!dvFileId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Download is not available for this dataset.
                </Alert>
              )}

              <Typography
                variant="body1"
                paragraph
                sx={{
                  mb: 1,
                  color: "text.secondary",
                  lineHeight: 1.6,
                }}
              >
                This dataset contains information about {datasetDisplayName}. It
                includes data from {yearsRange}
                {dataType
                  ? ` and is classified as ${formatDataTypeLabel(dataType)}`
                  : ""}
                {classification
                  ? ` with ${classification} classification`
                  : ""}
                {productLevel && productLevel !== "N/A"
                  ? ` at the ${productLevel}-digit level`
                  : ""}
                .
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  fontSize: 16,
                }}
              >
                <Typography variant="body1">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    File Name:
                  </Box>{" "}
                  {dataset.originalData?.dvFileName || "N/A"}
                </Typography>

                <Typography variant="body1">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    File Size:
                  </Box>{" "}
                  {dataset.originalData?.dvFileSize || "N/A"}
                </Typography>

                <Typography variant="body1">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Last Published:
                  </Box>{" "}
                  {formatDataPullDate(dataset.originalData?.dvPublicationDate) ||
                    "N/A"}
                </Typography>
              </Box>

              <Box
                sx={{
                  backgroundColor: "#CCD4DB",
                  padding: "16px",
                  marginBottom: "24px",
                  borderRadius: "0px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    flexWrap: "nowrap",
                  }}
                >
                  <CitationIcon
                    style={{
                      width: 26,
                      height: 26,
                      marginRight: "6px",
                    }}
                  />

                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      gap: 1,
                      fontWeight: 600,
                      fontSize: "18px",
                      textWrap: "nowrap",
                    }}
                  >
                    HOW TO CITE
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      px: 2,
                      color: "text.secondary",
                      "& a": {
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                    }}
                  >
                    The Growth Lab at Harvard University, {publicationYear}, "
                    {datasetDisplayName}",{" "}
                    {doi ? (
                      <a
                        href={doiUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {doiUrl}
                      </a>
                    ) : (
                      "http://www.atlas.hks.harvard.edu"
                    )}
                    , Harvard Dataverse
                  </Typography>
                </Box>
                <DatasetModalCopyButton
                  variant="primary"
                  size="sm"
                  onClick={handleCopyCitation}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  {copied ? "Copied" : "Copy"}
                </DatasetModalCopyButton>
              </Box>

              <Box sx={{ flex: 1 }}>
                <MetadataTable metadata={displayMetadata} />
              </Box>
          </Box>
        </ModalContentContainer>
      </ModalOverlay>
    </ModalRoot>
  );
};

export default DatasetModal;
